import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import CartLoader from "../../../../Helper/Loaders/CartLoader";
import { getDiagnosticsAnalyticsList } from "../../../../Api/BmsFaultsApi";
import { formatDateRange } from "../../../../Helper/Analytics/AnalyticsHelper";
import BreakdownChart from "../../../BmsFaults/Analytics/BreakdownChart";
import RenderChartLegend from "../../../AdvancedAnalytics/ContinuousChart/RenderChartLegend";
import BMSFaultChart from "../../../BmsFaults/Components/StyledComponents/BMSFaultChart";

const CHART_ID = "battery-triggered-fault-chart";

const LEGEND_SERIES = [
  { name: "Warning", color: "#FFBF3D" },
  { name: "High Severity", color: "#FF7A3D" },
  { name: "Critical", color: "#E03939" },
];

const transformChartData = (responseData) => {
  const transformedChartData = responseData.map(item => {
    const chartItem = {
      alertName: item.xLabel,
      warning: 0,
      high: 0,
      critical: 0,
    };
    item.metricsArray.forEach(metric => {
      if (metric.type === "warning") chartItem.warning = metric.value;
      else if (metric.type === "high severity") chartItem.high = metric.value;
      else if (metric.type === "critical") chartItem.critical = metric.value;
    });
    return chartItem;
  });
  return transformedChartData.sort((a, b) => {
    const totalA = (a.warning || 0) + (a.high || 0) + (a.critical || 0);
    const totalB = (b.warning || 0) + (b.high || 0) + (b.critical || 0);
    return totalB - totalA;
  });
};

const buildChartConfig = (chartData) => {
  if (!chartData || chartData.length === 0) {
    return { series: [], options: {}, categories: [] };
  }

  const categories = chartData.map(item => item.alertName);
  const rawWarningData = chartData.map(item => item.warning || 0);
  const rawHighData = chartData.map(item => item.high || 0);
  const rawCriticalData = chartData.map(item => item.critical || 0);

  const rawStackedTotals = rawWarningData.map((warning, index) =>
    warning + rawHighData[index] + rawCriticalData[index]
  );
  const maxValue = Math.max(...rawStackedTotals);
  const NO_VALUE_MIN_WIDTH = maxValue > 0 ? maxValue * 0.02 : 1;
  const MIN_WIDTH = maxValue > 0 ? maxValue * 0.03 : 1;

  const visualWarningData = rawWarningData.map((warning, index) => {
    const total = rawStackedTotals[index];
    if (total === 0) return NO_VALUE_MIN_WIDTH / 3;
    return (warning / total) * Math.max(total, MIN_WIDTH);
  });
  const visualHighData = rawHighData.map((high, index) => {
    const total = rawStackedTotals[index];
    if (total === 0) return NO_VALUE_MIN_WIDTH / 3;
    return (high / total) * Math.max(total, MIN_WIDTH);
  });
  const visualCriticalData = rawCriticalData.map((critical, index) => {
    const total = rawStackedTotals[index];
    if (total === 0) return NO_VALUE_MIN_WIDTH / 3;
    return (critical / total) * Math.max(total, MIN_WIDTH);
  });

  const visualStackedTotals = visualWarningData.map((w, i) => w + visualHighData[i] + visualCriticalData[i]);
  const xAxisMax = Math.max(...visualStackedTotals) * 1.1;

  const series = [
    { name: "Warning", data: visualWarningData },
    { name: "High Severity", data: visualHighData },
    { name: "Critical", data: visualCriticalData },
  ];

  const options = {
    chart: { id: CHART_ID, stacked: true },
    plotOptions: {
      bar: {
        dataLabels: {
          total: {
            enabled: true,
            formatter: (value, opts) => rawStackedTotals[opts.dataPointIndex],
            offsetX: 8,
            offsetY: 0,
            style: {
              color: "#494949",
              fontSize: "12px",
              fontFamily: "Roboto",
              fontWeight: 400,
            },
          },
        },
      },
    },
    colors: ["#FFBF3D", "#FF7A3D", "#E03939"],
    dataLabels: { enabled: false },
    states: { active: { filter: { type: "none" } } },
    xaxis: { categories, max: xAxisMax },
    legend: { show: false },
    tooltip: {
      y: {
        formatter: (val, opts) => {
          const { dataPointIndex, seriesIndex } = opts;
          const rawValues = [rawWarningData, rawHighData, rawCriticalData];
          return (rawValues[seriesIndex]?.[dataPointIndex] ?? val) + " faults";
        },
      },
    },
    grid: { padding: { bottom: 24 } },
  };

  return { series, options, categories };
};

const BatteryTriggeredFaultChart = ({ batteryID, startDate, endDate, onBarClick }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!batteryID || !startDate || !endDate) return;
    let cancelled = false;
    setLoading(true);
    getDiagnosticsAnalyticsList({}, {
      startDate,
      endDate,
      select: ["GeneratedFaultsCount"],
      filters: [{ name: "batteryID", values: [batteryID] }],
      groupBy: ["errorDescription", "FaultSeverityName"],
      granularity: null,
    }).then(response => {
      if (cancelled) return;
      const responseData = response?.response?.data || [];
      setChartData(transformChartData(responseData));
    }).catch(() => {
      if (!cancelled) setChartData([]);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [batteryID, startDate, endDate]);

  const { series, options, categories } = buildChartConfig(chartData);
  const hasData = series.length > 0 && categories.length > 0;

  // Merge dataPointSelection event into chart options when onBarClick is provided
  const chartOptions = useMemo(() => {
    if (!onBarClick) return options;
    return {
      ...options,
      chart: {
        ...options.chart,
        events: {
          ...(options.chart?.events || {}),
          dataPointSelection: (event, chartContext, config) => {
            const { dataPointIndex, seriesIndex } = config;
            if (dataPointIndex >= 0 && categories[dataPointIndex]) {
              const severityName = LEGEND_SERIES[seriesIndex]?.name || null;
              onBarClick(categories[dataPointIndex], severityName);
            }
          },
        },
      },
    };
  }, [options, onBarClick, categories]);

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        border: "1px solid #E6E6E6",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 22px",
        }}
      >
        <Typography variant="fw600s16lh24c444B51" sx={{ color: "#2E2E2E" }}>
          Triggered Battery System Fault Breakdown
        </Typography>
        <Typography variant="fw400s12c686868" sx={{ lineHeight: "20px" }}>
          {formatDateRange(startDate, endDate)}
        </Typography>
      </Box>

      <Box
        sx={{
          padding: "24px 28px",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "120px" }}>
            <CartLoader />
          </Box>
        ) : !hasData ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "120px" }}>
            <Typography variant="fw400s14c686868">No chart data available</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="fw600s12c8D8D8D" sx={{ textTransform: "uppercase", color: "#8F8F8F" }}>
                Fault Name
              </Typography>
              <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
                <RenderChartLegend
                  legendSeries={LEGEND_SERIES}
                  chartIDs={[CHART_ID]}
                  centered
                />
              </Box>
            </Box>
            <BMSFaultChart drillDownEnabled={!!onBarClick}>
              <BreakdownChart
                categories={categories}
                series={series}
                options={chartOptions}
                minVisibilityHeight={150}
                labelWidth="260px"
                tooltipOverflow={true}
              />
            </BMSFaultChart>
          </>
        )}
      </Box>
    </Box>
  );
};

export default BatteryTriggeredFaultChart;
